import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import SiteLayout from '@/components/feature/SiteLayout'
import PageHeader from '@/components/feature/PageHeader'
import WeddingStoriesSeo from '@/components/feature/WeddingStoriesSeo'
import StoryCard from '@/pages/weddingStories/components/StoryCard'
import FaqSection from '@/components/feature/sections/FaqSection'
import CtaBand from '@/components/feature/CtaBand'
import { weddingStories } from '@/mocks/weddingStories'
import { weddingStoriesFaqIds } from '@/mocks/weddingStoriesFaq'

export default function WeddingStories() {
  const { t } = useTranslation()
  const featured = weddingStories[0]
  const restStories = weddingStories.slice(1)
  const townCount = new Set(weddingStories.map((story) => story.place)).size

  const stats = [
    { id: 'stories', value: `${weddingStories.length}`, label: 'Wedding Stories Featured' },
    { id: 'towns', value: `${townCount}`, label: 'Towns Across Tamil Nadu' },
    { id: 'since', value: '1988', label: 'Photographing Since' },
  ]

  return (
    <SiteLayout>
      <WeddingStoriesSeo />
      <PageHeader
        breadcrumb={t('weddingStories.breadcrumb')}
        label={t('weddingStories.label')}
        title={t('weddingStories.title')}
        description={t('weddingStories.description')}
        image="https://readdy.ai/api/search-image?query=Warm%20cinematic%20collage%20of%20South%20Indian%20Tamil%20wedding%20moments%2C%20bride%20and%20groom%20with%20jasmine%20flowers%2C%20marigold%20decor%20and%20golden%20temple%20light%2C%20rich%20amber%20and%20gold%20editorial%20photography&width=1920&height=900&seq=shabi-hdr-stories&orientation=landscape"
        imageAlt="Real South Indian wedding stories photographed by Shabi Media across Tamil Nadu"
      />

      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 max-w-4xl">
            {stats.map((item) => (
              <div key={item.id} className="rounded-lg border border-background-200 bg-background-100/70 px-6 py-6">
                <span className="block font-heading text-4xl md:text-5xl font-semibold text-primary-600 leading-none">
                  {item.value}
                </span>
                <span className="mt-3 block text-xs md:text-sm tracking-[0.12em] uppercase text-foreground-500">
                  {item.label}
                </span>
              </div>
            ))}
          </div>

          <article className="mt-14 grid grid-cols-1 lg:grid-cols-2 rounded-lg overflow-hidden border border-background-200 bg-background-50 lg:min-h-[440px]">
            <div className="relative w-full h-72 md:h-96 lg:h-full overflow-hidden">
              <img
                src={featured.coverImage}
                alt={`${featured.couple} — featured ${featured.type} in ${featured.place} by Shabi Media`}
                title={`${featured.couple} | Featured wedding story, ${featured.place}`}
                className="w-full h-full object-cover object-top"
              />
              <span className="absolute top-5 left-5 inline-flex items-center gap-2 h-8 px-4 rounded-full bg-accent-500 text-background-50 text-[11px] font-medium tracking-[0.16em] uppercase">
                <i className="ri-award-line text-sm" />
                {t('weddingStories.featuredLabel')}
              </span>
            </div>

            <div className="flex flex-col p-7 md:p-10">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-primary-500" />
                <span className="section-label text-primary-700 text-[11px] md:text-xs font-medium">
                  {featured.type}
                </span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
                {featured.couple}
              </h2>
              <div className="mt-4 flex flex-wrap items-center gap-2 text-xs tracking-[0.14em] uppercase text-foreground-500">
                <i className="ri-map-pin-2-line text-base text-accent-600" />
                <span>{featured.place}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300" />
                <span>{featured.date}</span>
              </div>
              <p className="mt-5 text-base text-foreground-600 leading-relaxed flex-1">{featured.excerpt}</p>
              <Link
                to={featured.to}
                className="mt-7 inline-flex items-center justify-center gap-2 h-12 px-7 w-full sm:w-auto rounded-md bg-primary-500 text-background-50 text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                {t('weddingStories.readStory')}
                <i className="ri-arrow-right-line text-lg" />
              </Link>
            </div>
          </article>
        </div>
      </section>

      <section className="w-full bg-background-100 py-20 md:py-28">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-2xl mb-12 md:mb-16">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-primary-500" />
              <span className="section-label text-primary-700 text-[11px] md:text-xs font-medium">
                {t('weddingStories.breadcrumb')}
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight">
              {t('weddingStories.gridTitle')}
            </h2>
            <p className="mt-5 text-base text-foreground-600 leading-relaxed">{t('weddingStories.gridIntro')}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {restStories.map((story) => (
              <StoryCard key={story.id} story={story} />
            ))}
          </div>
        </div>
      </section>

      <FaqSection namespace="weddingStoriesFaq" ids={weddingStoriesFaqIds} />

      <CtaBand
        eyebrow={t('cta.weddingStories.eyebrow')}
        title={t('cta.weddingStories.title')}
        description={t('cta.weddingStories.description')}
      />
    </SiteLayout>
  )
}